/**
 * PREVENTION-ACTIONS-FILTER-PANEL.TSX
 * Filter risk cards by risk families, populations and places
 */

import useRiskFiltersSelection from "@/entities/physical-risk/hooks/useRiskFiltersSelection"
import ListItem from "@/components/list-item"
import PhysicalRisksSearch from "@/entities/physical-risk/ui/PhysicalRisksSearch"
import FilterSidePanel from "@/components/FilterSidePanel"
import { PopulationFilterBlock, RiskFamilyFilterBlock, SiteFilterBlock } from "@/components/FilterBlock"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import physicalRiskStore from "@/entities/physical-risk/model/physical-risk.store"
import populationStore from "@/entities/population/model/population.store"
import siteStore from "@/entities/site/model/site.store"
import { RISK_FAMILY_UNDEFINED_ID } from "@/entities/risk-family/model/risk-family.types"
import { flatten, uniq } from "lodash"

export default function PreventionActionsFilterPanel() {
  const dispatch = useAppDispatch()
  const selectedPhysicalRiskIds = useAppSelector(physicalRiskStore.selectors.selected)
  const selectedSiteIds = useAppSelector(siteStore.selectors.selected)
  const selectedPopulationIds = useAppSelector(populationStore.selectors.selected)
  const risks = useAppSelector(physicalRiskStore.selectors.list)
  const families = useAppSelector(physicalRiskStore.selectors.listFamilies)
  const populations = useAppSelector(populationStore.selectors.list)
  const sites = useAppSelector(siteStore.selectors.list)

  const familyIds = flatten(risks.map((risk) => risk.RiskFamilyId ?? RISK_FAMILY_UNDEFINED_ID))
  const selectedFamilies = families.filter((family) => familyIds.includes(family.id))
  const siteIds = uniq(flatten(risks.map((risk) => risk.siteIds)))
  const populationIds = uniq(flatten(risks.map((risk) => risk.populationIds)))
  const familyRiskIdsById = new Map(
    selectedFamilies.map((family) => [
      family.id,
      risks
        .filter((risk) =>
          family.id === RISK_FAMILY_UNDEFINED_ID ? !risk.RiskFamilyId : risk.RiskFamilyId === family.id
        )
        .map((risk) => risk.id)
    ])
  )
  const selectedFamilyCount = selectedFamilies.filter((family) => {
    const riskIds = familyRiskIdsById.get(family.id) ?? []
    return riskIds.length > 0 && riskIds.every((id) => selectedPhysicalRiskIds.includes(id))
  }).length
  const selectedFilterCount = selectedSiteIds.length + selectedPopulationIds.length + selectedFamilyCount
  const showAdd = selectedFilterCount > 0
  const showRemove = selectedFilterCount > 1
  const filteredSites = sites.filter((site) => siteIds.includes(site.id))
  const filteredPopulations = populations.filter((population) => populationIds.includes(population.id))
  const selectedSites = filteredSites.filter((site) => selectedSiteIds.includes(site.id))
  const selectedPopulations = filteredPopulations.filter((population) => selectedPopulationIds.includes(population.id))
  const selectedFamiliesSummary = selectedFamilies.filter((family) => {
    const riskIds = familyRiskIdsById.get(family.id) ?? []
    return riskIds.length > 0 && riskIds.every((id) => selectedPhysicalRiskIds.includes(id))
  })

  const { addPopulation, replaceWithSite, removeSite, replaceWithPopulation, removePopulation } =
    useRiskFiltersSelection()

  function replaceWithFamily() {
    dispatch(siteStore.actions.select([]))
    dispatch(populationStore.actions.select([]))
  }

  return (
    <FilterSidePanel width={400}>
      <ListItem>
        <PhysicalRisksSearch />
      </ListItem>
      <SiteFilterBlock
        sites={filteredSites}
        selectedSites={selectedSites}
        showAdd={showAdd}
        showRemove={showRemove}
        onReplace={replaceWithSite}
        onRemoveSite={removeSite}
      />
      <PopulationFilterBlock
        populations={filteredPopulations}
        selectedPopulationIds={selectedPopulationIds}
        selectedPopulations={selectedPopulations}
        showAdd={showAdd}
        showRemove={showRemove}
        onAddPopulation={addPopulation}
        onReplacePopulation={replaceWithPopulation}
        onRemovePopulation={removePopulation}
      />
      <RiskFamilyFilterBlock
        families={selectedFamilies}
        selectedFamiliesSummary={selectedFamiliesSummary}
        familyRiskIdsById={familyRiskIdsById}
        showAdd={showAdd}
        showRemove={showRemove}
        onReplace={replaceWithFamily}
      />
    </FilterSidePanel>
  )
}
